/**
 * The licensing artifact.
 *
 * **CME counts devices, not people**, and "the ability to access" is the
 * billable event rather than whether anyone looked. `docs/02` Constraint 6: a
 * distributor who cannot produce this list cannot answer an audit, which makes
 * this screen compliance infrastructure wearing an admin costume.
 *
 * Aggregated to per-exchange totals and naming nobody. That is not squeamishness
 * — it is what the report is *for*. An auditor asks how many subscribers and
 * how many devices per exchange; they do not ask which trader, and a report
 * that volunteered names would be answering a question nobody asked with data
 * nobody needed.
 *
 * Depth is counted separately because it is billed separately.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api, ApiError, type EntitlementReport } from '../../lib/api.js';
import type { ResolvedRange, SnapshotDay } from '@aegis/api-client';
import { RangePicker } from '../../components/RangePicker.js';

export function EntitlementsScreen(): JSX.Element {
  const [data, setData] = useState<EntitlementReport | null>(null);
  const [history, setHistory] = useState<readonly SnapshotDay<EntitlementReport>[]>([]);
  const [window, setWindow] = useState<ResolvedRange | null>(null);
  const [range, setRange] = useState('30d');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (r: string) => {
    try {
      const res = await api.entitlementReport(r);
      setData(res);
      setHistory(res.history);
      setWindow(res.range);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404
        ? 'This is not available on your account.'
        : 'Could not load the entitlement report.');
    }
  }, []);
  useEffect(() => { void load(range); }, [load, range]);

  if (!data) {
    return (
      <div className="scroll-area">
        <h2>Entitlements</h2>
        <p className="dim">{error ?? 'Loading…'}</p>
      </div>
    );
  }

  const exchanges = Object.entries(data.byExchange).sort((a, b) => b[1].devices - a[1].devices);

  /**
   * The counts as recorded, one row a day.
   *
   * This is the half an audit actually asks for. "How many devices do you have"
   * is answerable from live state; "how many did you report in March" is not,
   * because a revoked device leaves no trace of the month it was active.
   */
  const timeline = (
    <>
      <RangePicker
        value={range}
        onChange={setRange}
        resolved={window}
        note="Recorded once a day. History begins when snapshots were switched on."
      />
      {history.length === 0 ? (
        <p className="panel-body dim">
          Nothing recorded in this window. Snapshots start the day they were
          deployed — earlier dates have no rows rather than reconstructed ones.
        </p>
      ) : (
        <div className="table-scroll" tabIndex={0}>
          <table className="report-table">
            <thead>
              <tr><th>Day</th><th className="num">Subscribers</th><th className="num">Devices</th></tr>
            </thead>
            <tbody>
              {history.map((d) => (
                <tr key={d.day}>
                  <td className="mono">{d.day}</td>
                  <td className="num mono">{d.payload.subscribers}</td>
                  <td className="num mono">{d.payload.devices}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Entitlements</h2>
        <span className="chip">
          {data.devices} device{data.devices === 1 ? '' : 's'}
        </span>
      </div>

      <p className="panel-body">
        What an exchange audit asks for. Exchanges count <strong>devices</strong>,
        not people — the ability to access is the billable event, whether or not
        anyone looked.
      </p>
      <p className="panel-body dim">
        Per-exchange totals across every subscriber. Nobody is named here, because
        an audit asks how many and not who.
      </p>

      <section className="admin-block">
        <dl className="kv">
          <span className="kv-pair"><dt>Subscribers</dt><dd className="mono">{data.subscribers}</dd></span>
          <span className="kv-pair"><dt>Devices</dt><dd className="mono">{data.devices}</dd></span>
          <span className="kv-pair">
            <dt>Generated</dt>
            <dd className="mono">{new Date(data.generatedAt).toLocaleString()}</dd>
          </span>
        </dl>
      </section>

      <section className="admin-block">
        <h3 className="panel-h">BY EXCHANGE</h3>
        {exchanges.length === 0 ? (
          <p className="panel-body">
            No entitlements granted. Nothing is being billed, and nothing needs
            reporting.
          </p>
        ) : (
          <ul className="admin-list">
            {exchanges.map(([exchange, row]) => (
              <li key={exchange} className="admin-row admin-row-2">
                <span className="admin-name">{exchange}</span>
                <span className="admin-meta">
                  {row.subscribers} subscriber{row.subscribers === 1 ? '' : 's'}
                  {' · '}{row.devices} device{row.devices === 1 ? '' : 's'}
                  {/* Separate because it is billed separately, and an audit
                      asks for it as its own number. */}
                  {row.depthSubscribers > 0 ? ` · ${row.depthSubscribers} with depth` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="panel-body dim">
        A non-professional subscriber gets one market-data session. A subscriber
        permitted three or more concurrent logins must be reported as
        professional — roughly $140 per exchange rather than $9 — which is why
        every device is registered and revocable rather than merely counted.
      </p>

      <section>
        <div className="section-head"><h3>Over time</h3></div>
        {timeline}
      </section>
    </div>
  );
}
